import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Typography,
    Card,
    CardContent,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tabs,
    Tab,
    Paper,
    CircularProgress,
    LinearProgress,
    Alert,
    Snackbar,
    TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { styled } from '@mui/material/styles';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import dashboardService from 'services/dashboardService';

// Styled components para o gauge
const GaugeContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: 180,
    height: 90,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
}));

const GaugeBackground = styled(Box)(({ theme }) => ({
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '90px 90px 0 0',
    background: 'conic-gradient(from 180deg, #015641 0deg 204.48deg, #E0E0E0 204.48deg 360deg)',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '20%',
        left: '20%',
        right: '20%',
        bottom: '0',
        background: 'white',
        borderRadius: '70px 70px 0 0',
    }
}));

const GaugeText = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '45%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    zIndex: 1,
}));

// Styled components para os cards de métricas
const MetricCard = styled(Card)(({ theme }) => ({
    background: '#E0E0E0',
    borderRadius: 6,
    minHeight: 30,
    minWidth: 70,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    padding: '2px 4px',
    '&:hover': {
        boxShadow: theme.shadows[4],
    }
}));

const MetricValue = styled(Box)(({ theme }) => ({
    background: 'white',
    borderRadius: 4,
    padding: '2px 6px',
    marginTop: 2,
    fontWeight: 'bold',
    fontSize: '1rem',
    color: theme.palette.text.primary,
    border: '1px solid #E0E0E0',
    minWidth: 35,
    textAlign: 'center',
}));

const CriticalMetricValue = styled(MetricValue)(({ theme }) => ({
    border: '2px solid #F44336',
    color: '#F44336',
    background: '#FFEBEE',
}));

// Componente para conectar cards com linha vertical
const VerticalConnector = styled(Box)(({ theme }) => ({
    width: '2px',
    height: '20px',
    background: '#000',
    margin: '0 auto'
}));

const VerticalConnectorBacklog = styled(Box)(({ theme }) => ({
    width: '2px',
    height: '20px',
    background: '#000',
    margin: '0 auto',
    position: 'absolute',
    marginTop: '3%',
    marginRight: '4.8%',
}));

// Componente para seta horizontal
const HorizontalArrow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 10px',
    '&::after': {
        content: '"→"',
        fontSize: '2.5rem',
        color: '#000',
        fontWeight: 'bold',
    }
}));

// Componente do gauge
const SLAGauge = ({ value = 56.8 }) => {
    const percentage = Math.min(Math.max(value, 0), 100);
    const radius = 70;
    const strokeWidth = 20;
    const centerX = 90;
    const centerY = 90;
    
    // Calcular o ângulo baseado na porcentagem (180 graus = 100%)
    const angle = (percentage / 100) * 180;
    
    // Converter ângulos para coordenadas (começando da esquerda)
    const startAngle = 180; // Começar da esquerda
    const endAngle = 180 - angle; // Ir no sentido horário
    
    const startX = centerX + radius * Math.cos((startAngle) * Math.PI / 180);
    const startY = centerY + radius * Math.sin((startAngle) * Math.PI / 180);
    const endX = centerX + radius * Math.cos((endAngle) * Math.PI / 180);
    const endY = centerY + radius * Math.sin((endAngle) * Math.PI / 180);
    
    // Path para o arco de fundo (semicírculo completo)
    const backgroundPath = `M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`;
    
    // Path para o arco de progresso - usar o mesmo path do fundo mas com stroke-dasharray
    const circumference = Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;
    
    return (
        <GaugeContainer>
            <svg width="180" height="90" viewBox="0 0 180 90" style={{ position: 'absolute' }}>
                {/* Background arc (semicírculo completo) */}
                <path
                    d={backgroundPath}
                    fill="none"
                    stroke="#E0E0E0"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                />
                {/* Progress arc usando stroke-dasharray */}
                <path
                    d={backgroundPath}
                    fill="none"
                    stroke="#015641"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                />
            </svg>
            <GaugeText>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: '#000', fontSize: '1.8rem', marginTop: '50px' }}>
                    {percentage.toFixed(1)}%
                </Typography>
            </GaugeText>
        </GaugeContainer>
    );
};

// Componente principal do Dashboard
const Dashboard = () => {
    const [selectedIndicator, setSelectedIndicator] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    
    // Estados para dados da API
    const [dashboardData, setDashboardData] = useState({
        chamados: {
            abertos: 0,
            fechados: 0
        },
        sla: 0,
        backlog: 0,
        resolucao: {
            dentroPrazo: 0,
            foraPrazo: 0
        }
    });
    
    const [typeOsOptions, setTypeOsOptions] = useState([]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Carregar dados iniciais
    useEffect(() => {
        loadInitialData();
    }, []);

    // Carregar dados do dashboard quando filtros mudarem
    useEffect(() => {
        if (selectedDate) {
            loadDashboardData();
        }
    }, [selectedDate, selectedIndicator]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            
            // Carregar apenas tipos de OS
            const typeOsResponse = await dashboardService.getTypeOsOptions();

            if (typeOsResponse.success) {
                setTypeOsOptions(typeOsResponse.data);
            }

        } catch (error) {
            console.error('Erro ao carregar dados iniciais:', error);
            setError('Erro ao carregar dados iniciais');
            setSnackbarMessage('Erro ao carregar dados iniciais');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Converter data para formato esperado pela API (mês-ano)
            const month = selectedDate.getMonth() + 1; // getMonth() retorna 0-11
            const year = selectedDate.getFullYear();
            const monthNames = [
                'janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho',
                'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
            ];
            const period = `${monthNames[selectedDate.getMonth()]}-${year}`;

            // Se nenhum tipo de OS selecionado, enviar array vazio para não filtrar
            const typeOsFilter = selectedIndicator.length > 0 ? selectedIndicator : [];

            const response = await dashboardService.getDashboardSummary(
                period, 
                typeOsFilter
            );

            if (response.success) {
                setDashboardData(response.data);
            } else {
                throw new Error(response.message || 'Erro ao carregar dados');
            }

        } catch (error) {
            console.error('Erro ao carregar dados do dashboard:', error);
            setError('Erro ao carregar dados do dashboard');
            setSnackbarMessage('Erro ao carregar dados do dashboard');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleTypeOsChange = (event) => {
        const value = event.target.value;
        setSelectedIndicator(typeof value === 'string' ? value.split(',') : value);
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            {loading && (
                <Box sx={{ 
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    backgroundColor: 'rgba(0,0,0,0.3)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    zIndex: 9999 
                }}>
                    <Box sx={{ textAlign: 'center', color: 'white' }}>
                        <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
                        <Typography variant="h6">Carregando dados do dashboard...</Typography>
                    </Box>
                </Box>
            )}
            
            <MainCard spacing={gridSpacing} style={{ padding: 15, margin: 25 }}>
            {/* Header */}
            <Box sx={{ 
                background: 'linear-gradient(135deg, #015641 0%, #4CAF50 100%)',
                color: 'white',
                padding: 3,
                borderRadius: 2,
                marginBottom: 3
            }}>
                <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                            DASHBOARD GESTÃO DOS CHAMADOS
                        </Typography>
                    </Grid>
                    {/* <Grid item>
                        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            InovaBH
                        </Typography>
                    </Grid> */}
                </Grid>
            </Box>

            {/* Tabs de navegação */}
            <Paper sx={{ marginBottom: 3 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                        '& .MuiTab-root': {
                            backgroundColor: '#E0E0E0',
                            color: '#666',
                            '&.Mui-selected': {
                                backgroundColor: '#F5F5F5',
                                color: '#015641',
                                fontWeight: 'bold'
                            }
                        }
                    }}
                >
                    <Tab label="Dados Resumo" />
                    <Tab label="Planilha dados" />
                    <Tab label="Gráficos" />
                    <Tab label="Planejamento" />
                    <Tab label="Mapa" />
                </Tabs>
            </Paper>

            {/* Conteúdo principal */}
            <Card sx={{ padding: 3, minHeight: 500 }}>
                <CardContent>
                    {/* Filtros */}
                    <Grid container spacing={3} sx={{ marginBottom: 4 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Tipo de OS</InputLabel>
                                <Select
                                    multiple
                                    value={selectedIndicator}
                                    label="Tipo de OS"
                                    onChange={handleTypeOsChange}
                                    disabled={loading}
                                    helperText="Deixe vazio para incluir todos os tipos"
                                >
                                    {typeOsOptions.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <DatePicker
                                label="Selecionar Mês/Ano"
                                value={selectedDate}
                                onChange={handleDateChange}
                                views={['year', 'month']}
                                openTo="month"
                                disabled={loading}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        fullWidth 
                                        helperText="Selecione o mês e ano para visualizar os dados"
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    {/* Seção principal com CHAMADOS, SLA e BACKLOG */}
                    <Box sx={{ marginBottom: 4, position: 'relative' }}>
                        {/* Títulos superiores */}
                        <Typography variant="h6" sx={{ color: '#015641', fontWeight: 'bold', marginLeft: '10%', marginTop: '5%', fontSize: '3rem' }}>
                            📊 CHAMADOS
                        </Typography>
                        <Grid container spacing={4} sx={{ marginBottom: 3 }}>
                            <Grid item xs={4}>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                    {loading ? (
                                        <CircularProgress size={60} sx={{ color: '#015641' }} />
                                    ) : (
                                        <SLAGauge value={dashboardData.sla} />
                                    )}
                                    <Typography variant="h6" sx={{ color: '#015641', fontWeight: 'bold', marginTop: 1, fontSize: '0.9rem' }}>
                                        SLA de solução
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.7rem', marginTop: 0.5 }}>
                                        0.0% - 100.0%
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', textAlign: 'right' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold', fontSize: '2rem', marginRight: 1 }}>
                                            🔄 BACKLOG
                                        </Typography>
                                    </Box>
                                    <VerticalConnectorBacklog />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 1, fontSize: '2rem', marginRight: '10%' }}>
                                        <MetricValue sx={{ minWidth: 50, display: 'inline-block', fontSize: '2rem' }}>
                                            {loading ? <CircularProgress size={20} /> : dashboardData.backlog}
                                        </MetricValue>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    
                        {/* Cards de métricas - ABERTOS, FECHADOS, DENTRO DO PRAZO, FORA DO PRAZO */}
                        <Grid container spacing={2} alignItems="flex-start" justifyContent="center">
                            {/* ABERTOS */}
                            <Grid item xs={2.5}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <MetricCard>
                                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                            ABERTOS
                                        </Typography>
                                    </MetricCard>
                                    <VerticalConnector />
                                    <MetricValue>
                                        {loading ? <CircularProgress size={20} /> : dashboardData.chamados.abertos}
                                    </MetricValue>
                                </Box>
                            </Grid>

                            {/* FECHADOS */}
                            <Grid item xs={2.5}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <MetricCard>
                                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                            FECHADOS
                                        </Typography>
                                    </MetricCard>
                                    <VerticalConnector />
                                    <MetricValue>
                                        {loading ? <CircularProgress size={20} /> : dashboardData.chamados.fechados}
                                    </MetricValue>
                                </Box>
                            </Grid>

                            {/* Seta de conexão com SLA Gauge centralizado acima */}
                            <Grid item xs={1}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <HorizontalArrow />
                                </Box>
                            </Grid>

                            {/* DENTRO DO PRAZO */}
                            <Grid item xs={2.5}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <MetricCard>
                                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold', fontSize: '0.6rem', lineHeight: 1.2 }}>
                                            DENTRO DO PRAZO
                                        </Typography>
                                    </MetricCard>
                                    <VerticalConnector />
                                    <MetricValue>
                                        {loading ? <CircularProgress size={20} /> : dashboardData.resolucao.dentroPrazo}
                                    </MetricValue>
                                </Box>
                            </Grid>

                            {/* FORA DO PRAZO */}
                            <Grid item xs={2.5}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <MetricCard>
                                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold', fontSize: '0.6rem', lineHeight: 1.2 }}>
                                            FORA DO PRAZO
                                        </Typography>
                                    </MetricCard>
                                    <VerticalConnector />
                                <CriticalMetricValue>
                                    {loading ? <CircularProgress size={20} /> : dashboardData.resolucao.foraPrazo}
                                </CriticalMetricValue>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            </Card>

            {/* Footer */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: 3,
                padding: 2,
                color: '#666',
                fontSize: '0.9rem'
            }}>
                <Typography variant="body2">
                    Jope Infraestrutura Social Brasil
                </Typography>
                <Typography variant="body2">
                    {new Date().toLocaleDateString('pt-BR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                    })}
                </Typography>
            </Box>
            </MainCard>
            
            {/* Snackbar para erros */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSnackbarOpen(false)} 
                    severity="error" 
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </LocalizationProvider>
    );
};

export default Dashboard;
